import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { getSystemStats, getStatsRange, changeAdminPassword, SystemStats, StatsRange } from '../api.admin';

const getToken = () => localStorage.getItem('token') || '';

const COLORS = {
  users: '#2563eb',
  reservations: '#16a34a',
  apartments: '#ea580c',
};

const TRAFFIC_COLORS = {
  landing:  '#0891b2',
  register: '#7c3aed',
  login:    '#db2777',
};

// Date-range state for the two charts below. 'preset' = rolling N days ending today;
// 'month' = a specific calendar month, steppable with the arrows. Both resolve to the same
// [start, end] window and hit the same /admin/stats/range endpoint.
type RangeMode = 'preset' | 'month';
type PresetDays = 7 | 30 | 90;

const pad2 = (n: number) => String(n).padStart(2, '0');
const toISO = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

function computeWindow(mode: RangeMode, presetDays: PresetDays, selectedMonth: { year: number; month: number }) {
  if (mode === 'preset') {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (presetDays - 1));
    return { startISO: toISO(start), endISO: toISO(end), label: `Last ${presetDays} days` };
  }
  const { year, month } = selectedMonth;
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0); // day 0 of next month = last day of this month
  const label = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  return { startISO: toISO(start), endISO: toISO(end), label };
}

function isCurrentMonth(selectedMonth: { year: number; month: number }) {
  const now = new Date();
  return selectedMonth.year === now.getFullYear() && selectedMonth.month === now.getMonth() + 1;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwStatus, setPwStatus] = useState<null | 'saving' | 'ok' | 'error'>(null);
  const [pwError, setPwError] = useState<string | null>(null);

  // Range control, shared by both charts.
  const [rangeMode, setRangeMode] = useState<RangeMode>('preset');
  const [presetDays, setPresetDays] = useState<PresetDays>(30);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() + 1 };
  });
  const [range, setRange] = useState<StatsRange | null>(null);
  const [rangeLoading, setRangeLoading] = useState(true);

  // Totals row + security panel — not time-scoped, fetched once.
  useEffect(() => {
    const token = getToken();
    getSystemStats(token)
      .then(setStats)
      .catch(err => setError(err?.response?.data?.message || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  // Both charts — refetched whenever the range control changes.
  useEffect(() => {
    const token = getToken();
    const { startISO, endISO } = computeWindow(rangeMode, presetDays, selectedMonth);
    setRangeLoading(true);
    getStatsRange(token, startISO, endISO)
      .then(setRange)
      .catch(err => setError(err?.response?.data?.message || err?.message || 'Failed to load'))
      .finally(() => setRangeLoading(false));
  }, [rangeMode, presetDays, selectedMonth]);

  const shiftMonth = (delta: number) => {
    setRangeMode('month');
    setSelectedMonth(prev => {
      let { year, month } = prev;
      month += delta;
      if (month < 1) { month = 12; year -= 1; }
      if (month > 12) { month = 1; year += 1; }
      return { year, month };
    });
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    if (newPassword.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setPwError('Passwords do not match.'); return; }
    setPwStatus('saving');
    try {
      await changeAdminPassword(newPassword, getToken());
      setPwStatus('ok');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPwStatus('error');
      setPwError(err?.response?.data?.message || 'Failed to change password.');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading...</div>;
  if (error) return <div style={{ padding: 16, color: '#c53030', background: '#fff5f5', borderRadius: 8 }}>{error}</div>;
  if (!stats) return null;

  const { label: rangeLabel } = computeWindow(rangeMode, presetDays, selectedMonth);
  const monthLabel = rangeMode === 'month'
    ? rangeLabel
    : new Date(selectedMonth.year, selectedMonth.month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Build combined chart data (one row per day). Date shown as "MM-DD" instead of just the day
  // number — a 90-day preset spans up to 3 months, so "01".."31" alone would collide.
  const chartData = range ? range.users.map((u, i) => ({
    day: u.date.slice(5),
    Users: u.count,
    Reservations: range.reservations[i]?.count ?? 0,
    Apartments: range.apartments[i]?.count ?? 0,
  })) : [];

  // Build page traffic chart data
  const trafficData = range?.pageViews ? (() => {
    const byDate: Record<string, { Landing: number; Register: number; Login: number }> = {};
    const ensure = (d: string) => { if (!byDate[d]) byDate[d] = { Landing: 0, Register: 0, Login: 0 }; };
    range.pageViews.landing.forEach(r => { ensure(r.date); byDate[r.date].Landing = r.count; });
    range.pageViews.register.forEach(r => { ensure(r.date); byDate[r.date].Register = r.count; });
    range.pageViews.login.forEach(r => { ensure(r.date); byDate[r.date].Login = r.count; });
    return Object.entries(byDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, v]) => ({ day: date.slice(5), ...v }));
  })() : [];
  const totalLanding  = range?.pageViews?.landing.reduce((s, r) => s + r.count, 0) ?? 0;
  const totalRegister = range?.pageViews?.register.reduce((s, r) => s + r.count, 0) ?? 0;
  const totalLogin    = range?.pageViews?.login.reduce((s, r) => s + r.count, 0) ?? 0;

  // Thin out X-axis ticks once a range has more than ~10 days of points (the 90-day preset would
  // otherwise jam ~90 labels together).
  const tickInterval = (len: number) => Math.max(0, Math.ceil(len / 10) - 1);

  return (
    <div className="page-container">
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 2, color: '#1a202c' }}>Dashboard</h1>
      <p style={{ color: '#718096', marginBottom: 20, fontSize: '0.9rem' }}>System overview</p>

      {/* Totals row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <StatCard title="Total Users"    value={stats.totalUsers}        subtitle={`${stats.activeUsers} active`}        color="var(--brand)" />
        <StatCard title="Admins"         value={stats.adminUsers}         subtitle="System admins"                        color="#805ad5" />
        <StatCard title="Managers"       value={stats.managerUsers}       subtitle={`${stats.activeManagers} active`}     color="#16a34a" />
        <StatCard title="Apartments"     value={stats.totalApartments}    subtitle={`${stats.activeApartments} active`}   color="#ea580c" />
        <StatCard title="Reservations"   value={stats.totalReservations}  subtitle={`${stats.activeReservations} active`} color="var(--danger)" />
        <StatCard title="Guests"         value={stats.totalGuests}        subtitle="Registered"                           color="#319795" />
      </div>

      {/* Shared range control for both charts below */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10,
        marginBottom: 12,
      }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {([7, 30, 90] as PresetDays[]).map(d => (
            <button
              key={d}
              onClick={() => { setRangeMode('preset'); setPresetDays(d); }}
              style={presetButtonStyle(rangeMode === 'preset' && presetDays === d)}
            >
              {d}D
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button onClick={() => shiftMonth(-1)} style={arrowButtonStyle} aria-label="Previous month">‹</button>
          <button
            onClick={() => setRangeMode('month')}
            style={{ ...presetButtonStyle(rangeMode === 'month'), minWidth: 150 }}
          >
            {monthLabel}
          </button>
          <button
            onClick={() => shiftMonth(1)}
            disabled={rangeMode === 'month' && isCurrentMonth(selectedMonth)}
            style={{
              ...arrowButtonStyle,
              opacity: (rangeMode === 'month' && isCurrentMonth(selectedMonth)) ? 0.35 : 1,
              cursor: (rangeMode === 'month' && isCurrentMonth(selectedMonth)) ? 'default' : 'pointer',
            }}
            aria-label="Next month"
          >
            ›
          </button>
        </div>
      </div>

      {/* Monthly charts */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '20px 16px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', opacity: rangeLoading ? 0.6 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c' }}>Activity</div>
            <div style={{ color: '#718096', fontSize: '0.82rem' }}>{rangeLabel}</div>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <MonthBadge label="New Users"        value={range?.totalNewUsers ?? 0}        color={COLORS.users} />
            <MonthBadge label="New Reservations" value={range?.totalNewReservations ?? 0} color={COLORS.reservations} />
            <MonthBadge label="New Apartments"   value={range?.totalNewApartments ?? 0}   color={COLORS.apartments} />
          </div>
        </div>

        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="day"
              tick={{ fontSize: 11, fill: '#a0aec0' }}
              tickLine={false}
              axisLine={false}
              interval={tickInterval(chartData.length)}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#a0aec0' }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.82rem' }}
              labelFormatter={v => `Day ${v}`}
            />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '0.8rem', paddingTop: 8 }}
            />
            <Line type="monotone" dataKey="Users"        stroke={COLORS.users}        strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="Reservations" stroke={COLORS.reservations} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="Apartments"   stroke={COLORS.apartments}   strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Page traffic chart */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '20px 16px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', marginTop: 16, opacity: rangeLoading ? 0.6 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c' }}>Page traffic</div>
            <div style={{ color: '#718096', fontSize: '0.82rem' }}>{rangeLabel}</div>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <MonthBadge label="Landing"  value={totalLanding}  color={TRAFFIC_COLORS.landing} />
            <MonthBadge label="Register" value={totalRegister} color={TRAFFIC_COLORS.register} />
            <MonthBadge label="Login"    value={totalLogin}    color={TRAFFIC_COLORS.login} />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trafficData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#a0aec0' }} tickLine={false} axisLine={false} interval={tickInterval(trafficData.length)} />
            <YAxis tick={{ fontSize: 11, fill: '#a0aec0' }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={{ borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.82rem' }} labelFormatter={v => `Day ${v}`} />
            <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '0.8rem', paddingTop: 8 }} />
            <Line type="monotone" dataKey="Landing"  stroke={TRAFFIC_COLORS.landing}  strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="Register" stroke={TRAFFIC_COLORS.register} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
            <Line type="monotone" dataKey="Login"    stroke={TRAFFIC_COLORS.login}    strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Security — change admin password */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '20px 16px', boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0', marginTop: 16 }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c', marginBottom: 4 }}>Security</div>
        <div style={{ color: '#718096', fontSize: '0.82rem', marginBottom: 16 }}>Change admin password</div>
        <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 360 }}>
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={e => { setNewPassword(e.target.value); setPwStatus(null); setPwError(null); }}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.9rem' }}
            required
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={e => { setConfirmPassword(e.target.value); setPwStatus(null); setPwError(null); }}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.9rem' }}
            required
          />
          {pwError && <div style={{ color: '#c53030', fontSize: '0.82rem' }}>{pwError}</div>}
          {pwStatus === 'ok' && <div style={{ color: '#276749', fontSize: '0.82rem' }}>Password changed successfully.</div>}
          <button
            type="submit"
            disabled={pwStatus === 'saving'}
            style={{ alignSelf: 'flex-start', padding: '8px 20px', borderRadius: 6, background: '#2563eb', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}
          >
            {pwStatus === 'saving' ? 'Saving…' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

const presetButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: '6px 14px',
  borderRadius: 6,
  border: active ? '1px solid #2563eb' : '1px solid #e2e8f0',
  background: active ? '#2563eb' : '#fff',
  color: active ? '#fff' : '#4a5568',
  fontSize: '0.82rem',
  fontWeight: 600,
  cursor: 'pointer',
});

const arrowButtonStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#4a5568',
  fontSize: '0.9rem',
  fontWeight: 600,
  cursor: 'pointer',
  lineHeight: 1,
};

const StatCard: React.FC<{ title: string; value: number; subtitle: string; color: string }> = ({ title, value, subtitle, color }) => (
  <div style={{
    padding: '16px 18px',
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
    borderLeft: `4px solid ${color}`,
  }}>
    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
      {title}
    </div>
    <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1a202c', lineHeight: 1, marginBottom: 3 }}>
      {value}
    </div>
    <div style={{ fontSize: '0.78rem', color: '#a0aec0' }}>{subtitle}</div>
  </div>
);

const MonthBadge: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{ textAlign: 'right' }}>
    <div style={{ fontSize: '1.3rem', fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '0.72rem', color: '#a0aec0', marginTop: 2 }}>{label}</div>
  </div>
);

export default AdminDashboard;
